import { OPERATION_IDS } from '@finance/contracts';
import { ORGANIZATION_PERMISSIONS_LIST } from '@finance/contracts/operations';
import { investmentInstrumentList } from '../../api/generated/client';
import { httpClient, parseApiError } from '../../api/http-client';
import { selectGeneratedApiList } from '../../api/select-api-data';
import {
  getOptionAdapter,
  registerApiOptionFetcher,
  registerOptionAdapter,
} from './option-adapter-registry';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function extractRows(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (!isRecord(value)) throw new Error('OPTION_SOURCE_RESPONSE_MALFORMED');
  if (Array.isArray(value.items)) return value.items;
  if (isRecord(value.table) && Array.isArray(value.table.rows)) return value.table.rows;
  if ('data' in value) return extractRows(value.data);
  throw new Error('OPTION_SOURCE_ROWS_MISSING');
}

async function fetchRows(path: string, signal?: AbortSignal): Promise<unknown[]> {
  const response = await httpClient(path, { method: 'GET', signal });
  if (!response.ok) throw await parseApiError(response);
  return extractRows(await response.json());
}

function resourceFiltersPath(path: string, query?: Record<string, unknown>): string {
  if (!query || Object.keys(query).length === 0) return path;
  const params = new URLSearchParams();
  params.set('filters', JSON.stringify(query));
  return `${path}?${params.toString()}`;
}

export function registerApiOptionFetchers(): void {
  registerApiOptionFetcher(String(OPERATION_IDS.INSTRUMENT_LIST), async signal => {
    const response = await investmentInstrumentList({ signal });
    return selectGeneratedApiList<Record<string, unknown>>(response);
  });

  registerApiOptionFetcher(String(OPERATION_IDS.PLATFORM_USERS_LIST), signal =>
    fetchRows('/api/resources/platform-users', signal)
  );

  registerApiOptionFetcher(String(OPERATION_IDS.PLATFORM_ORGANIZATIONS_LIST), signal =>
    fetchRows('/api/resources/platform-organizations', signal)
  );

  registerApiOptionFetcher(String(OPERATION_IDS.ORGANIZATION_ROLES_LIST), signal =>
    fetchRows('/api/roles/surface', signal)
  );

  registerApiOptionFetcher(String(OPERATION_IDS.ORGANIZATION_BRANCHES_LIST), signal =>
    fetchRows('/api/organization/branches', signal)
  );

  registerApiOptionFetcher(`${String(OPERATION_IDS.ORGANIZATION_BRANCHES_LIST)}:active`, signal =>
    fetchRows('/api/organization/branches', signal).then(rows =>
      rows.filter((row) => isRecord(row) && row.status === 'ACTIVE'),
    )
  );

  registerApiOptionFetcher(String(OPERATION_IDS.ORGANIZATION_CLIENTS_LIST), signal =>
    fetchRows('/api/resources/organization-clients', signal)
  );

  registerApiOptionFetcher(
    String(OPERATION_IDS.ORGANIZATION_CLIENT_ACCOUNTS_LIST),
    (signal, query) =>
      fetchRows(
        resourceFiltersPath('/api/resources/organization-client-accounts', query),
        signal,
      )
  );

  registerApiOptionFetcher(String(OPERATION_IDS.ORGANIZATION_PORTFOLIOS_LIST), signal =>
    fetchRows('/api/resources/organization-portfolios', signal)
  );

  registerApiOptionFetcher(
    String(OPERATION_IDS.PORTFOLIO_ACCOUNT_LIST),
    (signal, query) => {
      const clientAccountId = query?.clientAccountId;
      const suffix = clientAccountId ? `?party=${encodeURIComponent(String(clientAccountId))}` : '';
      return fetchRows(`/api/accounts${suffix}`, signal);
    }
  );

  registerApiOptionFetcher(String(OPERATION_IDS.CHART_OF_ACCOUNTS_LIST), signal =>
    fetchRows('/api/accounting/coa/accounts?isActive=true', signal)
  );

  registerApiOptionFetcher(String(OPERATION_IDS.ORGANIZATION_MODULES_LIST_ASSIGNED), signal =>
    fetchRows('/api/organization/modules/options', signal)
  );

  const PERMISSIONS_CACHE_TTL_MS = 60_000;
  let permissionsCache: { promise: Promise<unknown[]>; expiry: number } | null = null;

  registerApiOptionFetcher(String(ORGANIZATION_PERMISSIONS_LIST), () => {
    const now = Date.now();
    if (permissionsCache && permissionsCache.expiry > now) {
      return permissionsCache.promise;
    }

    const promise = fetchRows('/api/admin/permissions').then(rows =>
      rows.map(row => {
        if (!isRecord(row) || typeof row.code !== 'string' || typeof row.label !== 'string') {
          throw new Error('PERMISSION_OPTION_MALFORMED');
        }
        // Role records store assigned permissions by canonical code, so option
        // values must be codes for the multiselect to match initial values.
        return { value: row.code, label: row.label };
      }),
    );

    permissionsCache = { promise, expiry: now + PERMISSIONS_CACHE_TTL_MS };
    return promise;
  });

  // Map canonical `experience.options.*` sources to the API option adapter.
  // This centralizes option-source wiring so each consumer does not need a
  // one-off adapter. The organization scoping is provided by the backend.
  const experienceOptionAdapters: Array<{ adapterId: string; operationId: string }> = [
    { adapterId: 'experience.options.branches', operationId: String(OPERATION_IDS.ORGANIZATION_BRANCHES_LIST) },
    { adapterId: 'experience.options.activeBranches', operationId: `${String(OPERATION_IDS.ORGANIZATION_BRANCHES_LIST)}:active` },
    { adapterId: 'experience.options.roles', operationId: String(OPERATION_IDS.ORGANIZATION_ROLES_LIST) },
    { adapterId: 'experience.options.permissions', operationId: String(ORGANIZATION_PERMISSIONS_LIST) },
  ];

  for (const { adapterId, operationId } of experienceOptionAdapters) {
    registerOptionAdapter(adapterId, async (source, ctx) => {
      const api = getOptionAdapter('api');
      if (!api) throw new Error('api option adapter is not registered');
      return api({ ...source, operationId: operationId as never }, ctx);
    });
  }
}
